<template>
    <div
        :style="[
            bgColor ? `background-color: ${bgColor}` : '',
            `padding-top: ${topPadding}px;padding-bottom: ${bottomPadding}px;`,
        ]"
    >
        <Container class="px-[18px]">
            <h2
                v-if="heading"
                class="text-[22px] md:text-[26px] font-[700] leading-tight mb-[28px]"
                :style="headingColor ? `color: ${headingColor}` : ''"
            >
                {{ heading }}
            </h2>

            <dl class="w-full">
                <div
                    v-for="(item, index) in items"
                    :key="item.id ?? index"
                    class="border-b"
                    :style="dividerColor ? `border-color: ${dividerColor}` : ''"
                >
                    <dt>
                        <button
                            type="button"
                            class="w-full flex items-center justify-between gap-[16px] text-left py-[16px] ps-[17px] pe-[16px] cursor-pointer group focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-current"
                            :style="questionColor ? `color: ${questionColor}` : ''"
                            :aria-expanded="isOpen(item, index) ? 'true' : 'false'"
                            :aria-controls="panelId(item, index)"
                            @click="toggle(item, index)"
                        >
                            <span class="text-[15px] md:text-[16px] font-[500]">
                                {{ item.question || 'Question goes here' }}
                            </span>

                            <span
                                class="shrink-0 w-[30px] h-[30px] rounded-full border flex items-center justify-center transition-colors duration-200 group-hover:bg-black/5"
                                aria-hidden="true"
                            >
                                <!-- A plus that loses its vertical stroke to become a minus. -->
                                <svg
                                    width="12"
                                    height="12"
                                    viewBox="0 0 12 12"
                                    fill="none"
                                    class="overflow-visible"
                                >
                                    <line
                                        x1="0.5" y1="6" x2="11.5" y2="6"
                                        stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                                    />
                                    <line
                                        x1="6" y1="0.5" x2="6" y2="11.5"
                                        stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                                        class="origin-center transition-all duration-200 ease-out"
                                        :class="isOpen(item, index) ? 'opacity-0 rotate-90' : 'opacity-100 rotate-0'"
                                    />
                                </svg>
                            </span>
                        </button>
                    </dt>

                    <!-- :css="false" hands timing entirely to the JS hooks below, so the
                         panel never waits on a transitionend event that may never fire. -->
                    <transition :css="false" @enter="onEnter" @leave="onLeave">
                        <dd
                            v-if="isOpen(item, index)"
                            :id="panelId(item, index)"
                        >
                            <div
                                class="faq-answer pb-[20px] ps-[17px] pe-[16px] text-[15px] leading-[1.65]"
                                :style="answerColor ? `color: ${answerColor}` : ''"
                            >
                                <div v-if="item.answer" v-html="item.answer"></div>
                                <p v-else class="italic opacity-60">
                                    No answer has been added for this question yet.
                                </p>
                            </div>
                        </dd>
                    </transition>
                </div>
            </dl>
        </Container>
    </div>
</template>

<script>
import Container from '../../../Layouts/Container.vue'

export default {
    components: {
        Container,
    },
    props: {
        isInEditor: {
            type: Boolean,
            default: false,
        },
        heading: {
            type: String,
            default: '',
        },
        items: {
            type: Array,
            default: () => [],
        },
        // Opening the first question by default helps visitors see that the
        // rows are interactive at all; off by default to match the mock.
        openFirstByDefault: {
            type: Boolean,
            default: false,
        },
        // Off = classic accordion, opening one question closes the previous.
        // On = every question toggles independently and several can be open.
        allowMultipleOpen: {
            type: Boolean,
            default: false,
        },
        bgColor: {
            type: String,
            default: null,
        },
        headingColor: {
            type: String,
            default: null,
        },
        questionColor: {
            type: String,
            default: null,
        },
        answerColor: {
            type: String,
            default: null,
        },
        dividerColor: {
            type: String,
            default: null,
        },
        topPadding: {
            type: Number,
            default: 50,
        },
        bottomPadding: {
            type: Number,
            default: 50,
        },
    },
    data() {
        return {
            // Keys of the open questions. Holds at most one entry unless
            // allowMultipleOpen is set, so single-open needs no separate branch
            // in the template.
            openKeys: [],
            // Namespaced so two FAQ widgets on one page cannot collide in the
            // aria-controls / id pairing.
            uid: Math.random().toString(36).slice(2, 9),
        }
    },
    created() {
        if (this.openFirstByDefault && this.items.length) {
            this.openKeys = [this.keyFor(this.items[0], 0)]
        }
    },
    watch: {
        // In the editor the items array is rebuilt as the editor types, and
        // reordering/deleting can strand a key on an item that is gone.
        items(newItems) {
            if (!this.openKeys.length) return

            const liveKeys = newItems.map((item, index) => this.keyFor(item, index))

            this.openKeys = this.openKeys.filter((key) => liveKeys.includes(key))
        },
        // Turning the toggle off in the editor while several answers are open
        // would otherwise leave the section in a state it can no longer reach.
        allowMultipleOpen(allowed) {
            if (!allowed && this.openKeys.length > 1) {
                this.openKeys = this.openKeys.slice(-1)
            }
        },
    },
    methods: {
        keyFor(item, index) {
            return item?.id ?? `index-${index}`
        },
        isOpen(item, index) {
            return this.openKeys.includes(this.keyFor(item, index))
        },
        toggle(item, index) {
            const key = this.keyFor(item, index)

            if (this.openKeys.includes(key)) {
                this.openKeys = this.openKeys.filter((open) => open !== key)
                return
            }

            this.openKeys = this.allowMultipleOpen ? [...this.openKeys, key] : [key]
        },
        panelId(item, index) {
            return `faq-${this.uid}-panel-${this.keyFor(item, index)}`
        },

        prefersReducedMotion() {
            return (
                typeof window !== 'undefined' &&
                typeof window.matchMedia === 'function' &&
                window.matchMedia('(prefers-reduced-motion: reduce)').matches
            )
        },

        // Height is animated from JS because a panel's natural height is not known
        // to CSS and `height: auto` is not an animatable value. The Web Animations
        // API is used rather than a CSS transition so that `done` is always called
        // exactly once -- a dropped transitionend would otherwise leave a collapsing
        // panel stuck open.
        animateHeight(el, from, to, done) {
            if (this.prefersReducedMotion() || typeof el.animate !== 'function') {
                done()
                return
            }

            const previousOverflow = el.style.overflow
            el.style.overflow = 'hidden'

            const animation = el.animate(
                [{ height: from }, { height: to }],
                { duration: 200, easing: 'ease-out' }
            )

            const settle = () => {
                el.style.overflow = previousOverflow
                done()
            }

            animation.onfinish = settle
            animation.oncancel = settle
        },
        onEnter(el, done) {
            this.animateHeight(el, '0px', `${el.scrollHeight}px`, done)
        },
        onLeave(el, done) {
            this.animateHeight(el, `${el.scrollHeight}px`, '0px', done)
        },
    },
}
</script>

<style scoped>
/* The answer body is editor-authored HTML, so give the common tags sane
   rhythm instead of leaving them to the page's base styles. */
.faq-answer :deep(p) {
    margin-bottom: 0.75rem;
}

.faq-answer :deep(p:last-child) {
    margin-bottom: 0;
}

.faq-answer :deep(a) {
    text-decoration: underline;
}

.faq-answer :deep(ul),
.faq-answer :deep(ol) {
    margin: 0.5rem 0 0.75rem;
    padding-inline-start: 1.25rem;
}

.faq-answer :deep(ul) {
    list-style: disc;
}

.faq-answer :deep(ol) {
    list-style: decimal;
}

.faq-answer :deep(li) {
    margin-bottom: 0.25rem;
}
</style>
